import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLatest } from 'react-use';
import styled, { css } from 'styled-components';
import { produce } from 'immer';
import { debounce, noop } from 'lodash';
import { Select, type SelectProps } from 'antd';
import InputTag from './InputTag';

export interface BaseSelectProps extends Omit<SelectProps, 'onChange'> {
  delay?: number;
  enableFreeInput?: boolean;
  onRemoteSearch?: (value: string) => Promise<{ label: string; value: string }[]> | undefined;
  onChange?: (value: SelectProps['value']) => void;
}

const StyledSelect = styled(Select)`
  ${(props) => {
    const tagStyles =
      props.mode === 'tags'
        ? css`
            .ant-select-selection-overflow {
              margin: 2px 0;
              gap: 4px;

              .ant-select-selection-overflow-item > span {
                display: inline-flex;
                max-width: 100%;
              }
            }
          `
        : '';

    return css`
      // Override antd default style
      &.ant-select-multiple.ant-select-disabled .ant-select-selection-item {
        margin-top: 0;
        margin-bottom: 0;
      }

      ${tagStyles}
    `;
  }}
` as typeof Select;

export const SearchSelect = (props: BaseSelectProps) => {
  const {
    mode,
    enableFreeInput = false,
    delay = 100,
    onRemoteSearch,
    value,
    onChange,
    ...restProps
  } = props;
  const [loading, setLoading] = useState(false);
  const onRemoteSearchRef = useLatest(onRemoteSearch || (noop as any));
  const searchIdRef = useRef(0);
  const [options, setOptions] = useState<SelectProps['options']>(
    props.options ? [...props.options] : [],
  );

  useEffect(() => {
    // reset options when props.options change
    setOptions(props.options ? [...props.options] : []);
  }, [props.options]);

  const handleOnSearch = useMemo(() => {
    const handle = debounce(async (text: string) => {
      const newOptions =
        enableFreeInput && text
          ? [
              {
                value: text,
                label: text,
              },
            ]
          : [];

      // if text length is less than 3, don't search
      if (text.length > 2) {
        const searchId = ++searchIdRef.current;
        setLoading(true);

        try {
          const remoteData = await Promise.resolve(onRemoteSearchRef.current(text));
          newOptions.push(...(remoteData || []));
        } catch (e) {
          // nothing
        }

        if (searchId !== searchIdRef.current) {
          return;
        }
      }

      setOptions(newOptions);
      setLoading(false);
    }, delay);
    return handle;
  }, [delay, enableFreeInput, onRemoteSearchRef]);

  const confirmInput = useCallback(
    (key: string, input: string) => {
      if (!input) return false;
      if (key === input) return true;

      const existedId = value.findIndex((v) => v.value === input);

      if (existedId !== -1) return false;

      const nextValue = produce(value, (draft) => {
        const idx = draft.findIndex((v) => v.value === key);

        if (idx !== -1) {
          draft[idx].value = input;
          draft[idx].label = input;
        }
      });
      const nextOptions = options?.filter((v) => v.value !== key);
      setOptions(nextOptions);
      onChange?.(nextValue);

      return true;
    },
    [options, onChange, value],
  );

  const tagRender = useCallback<NonNullable<SelectProps['tagRender']>>(
    (props) => {
      return <InputTag {...props} onChange={confirmInput} />;
    },
    [confirmInput],
  );

  return (
    <StyledSelect
      labelInValue
      allowClear
      showSearch
      mode={mode}
      value={value}
      loading={loading}
      options={options}
      tagRender={enableFreeInput && mode === 'tags' ? tagRender : undefined}
      placeholder="Input value"
      onChange={onChange}
      onSearch={handleOnSearch}
      onBlur={() => {
        handleOnSearch.cancel();
        setLoading(false);
      }}
      {...restProps}
    />
  );
};
